import { Icon, IconButton, IconOptions } from '@jfs/ui-new'
import { Box } from '@jfs/ui-new/src/comps/BoxNew'
import { SpacerNew } from '@jfs/ui-new/src/comps/SpacerNew'
import { Text } from '@jfs/ui-new/src/comps/TextNew'
import styles from '@jfs/ui-new/src/comps/TextNew/styles'
import {
  ColorOptions,
  FontSizeOptions,
  FontWeightOptions,
  RadiiOptions,
  SpaceOptions,
} from '@jfs/ui-new/src/theme/UiThemeProvider'
import { useBreakPointProps } from '@jfs/ui-new/src/utils/utils'
import React, { FC, useContext, useEffect, useRef, useState } from 'react'
import {
  KeyboardTypeOptions,
  NativeSyntheticEvent,
  Platform,
  TextInput as RNTextInput,
  ReturnKeyTypeOptions,
  TextInputKeyPressEventData,
  TextInputSubmitEditingEventData,
} from 'react-native'
import styled, { ThemeContext } from 'styled-components/native'
import { FlexboxProps, color, flexbox, layout, space, typography } from 'styled-system'
import { ModalAlertView } from '../ModalAlertView'
import { ToolTipView } from '../ToolTipView'

const StyledTextInput = styled.TextInput<FlexboxProps>`
  ${color}
  ${layout}
  ${typography}
  ${flexbox}
  ${space}
`

type FontWeightValues = 'normal' | 'bold' | '100' | '200' | '300' | '400' | '500' | '600' | '700' | '800' | '900'
export interface TextInputLabelToolTipProps {
  type?: TypesOption
  innerRef?: React.RefObject<RNTextInput>
  fontSize?: FontSizeOptions | number | Array<FontSizeOptions | number>
  fontWeight?: FontWeightOptions | FontWeightValues | Array<FontWeightOptions | FontWeightValues>
  color?: ColorOptions | Array<ColorOptions>
  labelColor?: ColorOptions | Array<ColorOptions>
  selectionColor?: ColorOptions | Array<ColorOptions>
  label?: string
  labelFontFamily?: string
  value?: string
  tintColor?: ColorOptions
  onChange?: (text: string) => void
  onChangeText?: (text: string) => void
  placeholder?: string
  placeholderTextColor?: ColorOptions | Array<ColorOptions>
  showBottomLine?: boolean
  bottomLineColor?: ColorOptions | Array<ColorOptions>
  onFocusBottomLineColor?: ColorOptions | Array<ColorOptions>
  isSecret?: boolean
  maxLength?: number | Array<number>
  leftComponent?: React.ReactNode
  rightComponent?: React.ReactNode
  rightIcon?: IconOptions
  rightAction?: string
  rightOnPress?: () => void
  keyboardType?: KeyboardTypeOptions
  editable?: boolean
  multiline?: boolean
  numberOfLines?: number
  onBlur?: () => void
  onFocus?: () => void
  onKeyPress?: (e: NativeSyntheticEvent<TextInputKeyPressEventData>) => void
  secureTextEntry?: boolean
  autoCompleteType?:
    | 'cc-csc'
    | 'cc-exp'
    | 'cc-exp-month'
    | 'cc-exp-year'
    | 'cc-number'
    | 'email'
    | 'name'
    | 'password'
    | 'postal-code'
    | 'street-address'
    | 'tel'
    | 'username'
    | 'off'
  autoCapitalize?: 'none' | 'sentences' | 'words' | 'characters'
  autoCorrect?: boolean
  onPressIn?: () => void
  onPressOut?: () => void
  error?: string
  errorColor?: ColorOptions
  touched?: boolean
  autoFocus?: boolean
  weight?: 'normal' | 'dense' | Array<'normal' | 'dense'>
  textAlign?: 'auto' | 'left' | 'right' | 'center' | 'justify' | Array<'auto' | 'left' | 'right' | 'center' | 'justify'>
  textWidth?: number | Array<number>
  hide?: boolean
  returnKeyType?: ReturnKeyTypeOptions
  onSubmitEditing?: (e: NativeSyntheticEvent<TextInputSubmitEditingEventData>) => void
  testID?: string
  padding?: SpaceOptions | number | Array<SpaceOptions | number>
  paddingHorizontal?: SpaceOptions | number | Array<SpaceOptions | number>
  paddingVertical?: SpaceOptions | number | Array<SpaceOptions | number>
  borderColor?: ColorOptions | Array<ColorOptions>
  focusBorderColor?: ColorOptions | Array<ColorOptions>
  labelPaddingBottom?: SpaceOptions | number
  borderRadius?: RadiiOptions | string | number | Array<RadiiOptions | string | number>
  minHeight?: number
  allValues?: any
  fieldName?: string
  onFocusBorderWidth?: number
  labelToolTipInfo?: any
  modalDetails?: any
  bgColor?: ColorOptions | Array<ColorOptions>
}

type TypesOption = keyof typeof styles
export const TextTypesOptionArray = Object.keys(styles)

const removeOutline =
  Platform.OS === 'web'
    ? {
        outline: 'none',
      }
    : {}

export const TextInputLabelToolTip: FC<TextInputLabelToolTipProps> = (props) => {
  const modifiedProps = useBreakPointProps<TextInputLabelToolTipProps>(props)
  const {
    value = '',
    type,
    onChange: onChangeText,
    label,
    labelColor = 'BRAND_COLOR_GRAY_1',
    labelFontFamily = 'jiotype-medium',
    innerRef,
    leftComponent = null,
    rightComponent = null,
    rightIcon,
    rightAction,
    rightOnPress,
    onPressIn,
    onPressOut,
    touched,
    error,
    errorColor,
    weight = 'normal',
    textWidth,
    hide = false,
    onFocus,
    onBlur,
    placeholderTextColor: propPlaceholderTextColor,
    testID,
    selectionColor: propSelectionColor,
    padding,
    paddingHorizontal,
    paddingVertical,
    borderColor = 'TRANSPARENT',
    focusBorderColor = 'BRAND_COLOR_GRAY_1',
    labelPaddingBottom = 16,
    borderRadius = 10,
    onFocusBorderWidth = 1,
    minHeight = undefined,
    labelToolTipInfo,
    modalDetails,
    editable,
    bgColor = 'BRAND_THEME',
    ...restProps
  } = modifiedProps
  const textInputRef = useRef<RNTextInput>(null)
  const [isFocused, setFocused] = useState(false)
  const [isPressed, setPressed] = useState(false)
  const [showError, setShowError] = useState(false)
  const [boxBorderColor, setBorderColor] = useState(borderColor)
  const [borderWidth, setBorderWidth] = useState(1)
  const errorTimerRef = useRef<any>(0)
  const theme = useContext(ThemeContext)
  const [onShowDetails, setShowDetails] = useState(false)

  let selectionColor = propSelectionColor
  if (selectionColor && theme?.colors[selectionColor as ColorOptions]) {
    selectionColor = theme?.colors[selectionColor as ColorOptions]
  }
  let placeholderTextColor = propPlaceholderTextColor
  if (placeholderTextColor && theme?.colors[placeholderTextColor as ColorOptions]) {
    placeholderTextColor = theme?.colors[placeholderTextColor as ColorOptions]
  }
  const isError = !isFocused && touched && !!error && !isPressed
  useEffect(() => {
    if (isError) {
      errorTimerRef.current = setTimeout(() => {
        setShowError(true)
      }, 500)
    } else {
      clearTimeout(errorTimerRef.current)
      setShowError(false)
    }
  }, [isError])

  useEffect(() => {
      setBorderColor(borderColor)
  }, [borderColor])

  if (hide) {
    return null
  }

  const refUsed = innerRef ?? textInputRef

  const onPress = () => {
    if (refUsed.current) {
      refUsed.current.focus()
      setBorderColor(focusBorderColor)
      setBorderWidth(onFocusBorderWidth)
    }
  }
  const onInternalFocus = () => {
    setFocused(true)
    if (onFocus) {
      onFocus()
    }
  }
  const onInternalBlur = () => {
    setFocused(false)
    setBorderColor(borderColor)
    setBorderWidth(1)
    if (onBlur) {
      onBlur()
    }
  }

  let right = null

  if (rightComponent) {
    right = rightComponent
  } else if (rightIcon) {
    if (!rightOnPress) {
      throw Error('rightOnPress is required')
    }
    right = <IconButton onPress={rightOnPress} color={'TEXT_PRIMARY'} id={rightIcon as IconOptions} />
  } else if (rightAction) {
    if (!rightOnPress) {
      throw Error('rightOnPress is required')
    }
    right = (
      <Box underlayColor={'transparent' as ColorOptions} onPress={rightOnPress}>
        <Text type={'tertiaryAction12'}>{rightAction}</Text>
      </Box>
    )
  }

  const typeStyle = type ? styles[type][weight as 'normal' | 'dense'] : {}
  return (
    <Box>
      {label && (
        <Box direction="row">
          <Box>
            <Text color={labelColor} type={type} title={label} fontFamily={labelFontFamily} />
            <SpacerNew size={labelPaddingBottom} />
          </Box>
          {labelToolTipInfo && labelToolTipInfo?.length > 0 && <ToolTipView value={labelToolTipInfo} top={0} />}
          {modalDetails?.data?.length > 0 && (
            <Box paddingLeft={6} underlayColor="TRANSPARENT" onPress={() => setShowDetails(true)}>
              <Icon id="INFO" size={16} color="ORANGE" />
            </Box>
          )}
          {modalDetails?.data?.length > 0 && onShowDetails && (
            <ModalAlertView
              show={true}
              title={label}
              buttonTitle=""
              message={modalDetails?.data?.replaceAll('|', '\n')}
              onDismiss={() => setShowDetails(false)}
              onCancel={() => setShowDetails(false)}
              height={500}
            />
          )}
        </Box>
      )}
      <Box
        backgroundColor={bgColor}
        customBorderStyle={'borderPrimary'}
        hoverColor={ editable?'BRAND_COLOR_GRAY_6':"TRANSPARENT"}
        underlayColor={ editable?'BRAND_COLOR_GRAY_5':"TRANSPARENT"}
        borderColor={boxBorderColor as ColorOptions}
        borderRadius={borderRadius}
        borderWidth={borderWidth}
        padding={padding}
        paddingHorizontal={paddingHorizontal}
        minHeight={minHeight}
        paddingVertical={paddingVertical}
        >
          <Box
            alignSelf="stretch"
            alignItems="center"
            flexDirection="row"
            onPress={onPress}
            onPressIn={() => {
              setPressed(true)
              if (onPressIn) {
                onPressIn()
              }
            }}
            onPressOut={() => {
              setPressed(false)
              if (onPressOut) {
                onPressOut()
              }
            }}
            underlayColor={'TRANSPARENT'}
            overflow="hidden">
            {leftComponent}
            {leftComponent && <SpacerNew direction="horizontal" size={8} />}
            <Box f={1}>
              <StyledTextInput
                padding={0}
                width={textWidth}
                ref={refUsed}
                onChangeText={onChangeText}
                onPressIn={onPressIn}
                onPressOut={onPressOut}
                onFocus={onInternalFocus}
                onBlur={onInternalBlur}
                fontWeight="700"
                fontSize={16}
                testID={testID}
                selectionColor={selectionColor}
                placeholderTextColor={placeholderTextColor}
                minHeight={minHeight}
                underlineColorAndroid={'transparent'}
                value={value}
                editable={editable}
                {...typeStyle}
                {...restProps}
                style={removeOutline}
              />
            </Box>
            {right && <SpacerNew direction="horizontal" size={8} />}
            {right}
          </Box>
      </Box>

      {showError && (
        <>
          <SpacerNew size={2} />
          <Text color={errorColor ? errorColor : 'ERROR'} type="body12">
            {error}
          </Text>
        </>
      )}
    </Box>
  )
}
